import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/Card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/Table';
import { Badge } from './ui/Badge';
import { Input } from './ui/Input';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { formatCurrency, cn } from '../lib/utils';
import { DollarSign, FileText, TrendingUp, Users, Search, ArrowUpRight, ArrowDownRight, Activity } from 'lucide-react';
import { MOCK_USERS, LEAD_SOURCES, INITIAL_CDS_TIERS } from '../data/mockData';
export function Overview() {
  const [search, setSearch] = useState('');
  // Calculate derived metrics
  const metrics = useMemo(() => {
    let totalBpsCommissions = 0;
    let totalCdsCommissions = 0;
    let totalFunded = 0;
    let lastMonthFunded = 0;
    const sourceVolumes: Record<string, {
      files: number;
      volume: number;
    }> = {};
    LEAD_SOURCES.forEach(s => sourceVolumes[s] = {
      files: 0,
      volume: 0
    });
    const usersWithCommissions = MOCK_USERS.map(user => {
      let commission = 0;
      totalFunded += user.fundedMTD;
      lastMonthFunded += user.fundedLastMonth;
      if (user.model === 'CDS') {
        const tier = INITIAL_CDS_TIERS.find(t => user.fundedMTD >= t.min && (t.max === null || user.fundedMTD <= t.max));
        commission = tier ? user.fundedMTD * tier.amount : 0;
        totalCdsCommissions += commission;
      } else if (user.model === 'BPS' && user.fundedBySource && user.rates) {
        Object.entries(user.fundedBySource).forEach(([source, data]) => {
          const bps = user.rates![source] || 0;
          const sourceCommission = data.volume * (bps / 10000);
          commission += sourceCommission;
          if (sourceVolumes[source]) {
            sourceVolumes[source].files += data.count;
            sourceVolumes[source].volume += data.volume;
          }
        });
        totalBpsCommissions += commission;
      }
      return {
        ...user,
        estimatedCommission: commission
      };
    });
    const topSources = Object.entries(sourceVolumes).map(([name, data]) => ({
      name,
      ...data
    })).filter(s => s.files > 0).sort((a, b) => b.files - a.files).slice(0, 6); // Show top 6 in cockpit
    return {
      totalCommissions: totalBpsCommissions + totalCdsCommissions,
      totalFunded,
      lastMonthFunded,
      fundedTrend: (totalFunded - lastMonthFunded) / Math.max(1, lastMonthFunded) * 100,
      bpsUsers: MOCK_USERS.filter(u => u.model === 'BPS').length,
      cdsUsers: MOCK_USERS.filter(u => u.model === 'CDS').length,
      topSources,
      users: usersWithCommissions.sort((a, b) => b.estimatedCommission - a.estimatedCommission)
    };
  }, []);
  const filteredUsers = metrics.users.filter(u => u.name.toLowerCase().includes(search.toLowerCase()) || u.title.toLowerCase().includes(search.toLowerCase()));
  // Mock monthly trending data
  const monthlyData = [{
    name: 'Jan',
    bpsCommissions: 450000,
    cdsCommissions: 120000
  }, {
    name: 'Feb',
    bpsCommissions: 520000,
    cdsCommissions: 145000
  }, {
    name: 'Mar',
    bpsCommissions: 480000,
    cdsCommissions: 110000
  }, {
    name: 'Apr',
    bpsCommissions: 610000,
    cdsCommissions: 180000
  }, {
    name: 'May',
    bpsCommissions: 590000,
    cdsCommissions: 165000
  }, {
    name: 'Jun',
    bpsCommissions: 680000,
    cdsCommissions: 210000
  }];
  return <div className="space-y-6" data-id="element-112">
      <div className="flex items-center justify-between" data-id="element-113">
        <div data-id="element-114">
          <h2 className="text-2xl font-bold tracking-tight text-slate-900" data-id="element-115">
            Commission Manager
          </h2>
          <p className="text-slate-500" data-id="element-116">
            Real-time commission and volume monitoring.
          </p>
        </div>
        <Badge variant="outline" className="bg-white px-3 py-1 shadow-sm" data-id="element-117">
          <Activity className="mr-2 h-4 w-4 text-blue-500" data-id="element-118" />
          Live MTD Data
        </Badge>
      </div>

      {/* Row 1: KPI Cockpit */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4" data-id="element-119">
        <Card className="bg-white shadow-sm border-slate-200/60" data-id="element-120">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2" data-id="element-121">
            <CardTitle className="text-sm font-semibold text-slate-600" data-id="element-122">
              Total Commissions
            </CardTitle>
            <div className="p-2 bg-blue-50 rounded-md" data-id="element-123">
              <DollarSign className="h-4 w-4 text-blue-600" data-id="element-124" />
            </div>
          </CardHeader>
          <CardContent data-id="element-125">
            <div className="text-3xl font-bold tracking-tight text-slate-900" data-id="element-126">
              {formatCurrency(metrics.totalCommissions)}
            </div>
            <p className="text-xs text-slate-500 mt-1 font-medium" data-id="element-127">
              Projected across all users
            </p>
          </CardContent>
        </Card>
        <Card className="bg-white shadow-sm border-slate-200/60" data-id="element-128">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2" data-id="element-129">
            <CardTitle className="text-sm font-semibold text-slate-600" data-id="element-130">
              Funded Files
            </CardTitle>
            <div className="p-2 bg-emerald-50 rounded-md" data-id="element-131">
              <FileText className="h-4 w-4 text-emerald-600" data-id="element-132" />
            </div>
          </CardHeader>
          <CardContent data-id="element-133">
            <div className="text-3xl font-bold tracking-tight text-slate-900" data-id="element-134">
              {metrics.totalFunded}
            </div>
            <div className="flex items-center text-xs mt-1" data-id="element-135">
              {metrics.fundedTrend >= 0 ? <span className="text-emerald-600 flex items-center font-semibold bg-emerald-50 px-1.5 py-0.5 rounded-sm" data-id="element-136">
                  <ArrowUpRight className="h-3 w-3 mr-1" data-id="element-137" />{' '}
                  {metrics.fundedTrend.toFixed(1)}%
                </span> : <span className="text-red-600 flex items-center font-semibold bg-red-50 px-1.5 py-0.5 rounded-sm" data-id="element-138">
                  <ArrowDownRight className="h-3 w-3 mr-1" data-id="element-139" />{' '}
                  {Math.abs(metrics.fundedTrend).toFixed(1)}%
                </span>}
              <span className="text-slate-500 ml-2 font-medium" data-id="element-140">
                vs last month
              </span>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white shadow-sm border-slate-200/60" data-id="element-141">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2" data-id="element-142">
            <CardTitle className="text-sm font-semibold text-slate-600" data-id="element-143">
              Active Users
            </CardTitle>
            <div className="p-2 bg-indigo-50 rounded-md" data-id="element-144">
              <Users className="h-4 w-4 text-indigo-600" data-id="element-145" />
            </div>
          </CardHeader>
          <CardContent data-id="element-146">
            <div className="text-3xl font-bold tracking-tight text-slate-900" data-id="element-147">
              {MOCK_USERS.length}
            </div>
            <p className="text-xs text-slate-500 mt-1 font-medium" data-id="element-148">
              <span className="text-slate-700" data-id="element-149">{metrics.bpsUsers}</span> BPS{' '}
              <span className="mx-1 text-slate-300" data-id="element-150">|</span>{' '}
              <span className="text-slate-700" data-id="element-151">{metrics.cdsUsers}</span> CDS
            </p>
          </CardContent>
        </Card>
        <Card className="bg-white shadow-sm border-slate-200/60" data-id="element-152">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2" data-id="element-153">
            <CardTitle className="text-sm font-semibold text-slate-600" data-id="element-154">
              Top Lead Source
            </CardTitle>
            <div className="p-2 bg-amber-50 rounded-md" data-id="element-155">
              <TrendingUp className="h-4 w-4 text-amber-600" data-id="element-156" />
            </div>
          </CardHeader>
          <CardContent data-id="element-157">
            <div className="text-3xl font-bold tracking-tight text-slate-900 truncate" data-id="element-158">
              {metrics.topSources[0]?.name || 'N/A'}
            </div>
            <p className="text-xs text-slate-500 mt-1 font-medium" data-id="element-159">
              {metrics.topSources[0]?.files || 0} files MTD
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Row 2: Charts & Sources */}
      <div className="grid gap-6 lg:grid-cols-3" data-id="element-160">
        <Card className="lg:col-span-2 shadow-sm border-slate-200/60" data-id="element-161">
          <CardHeader data-id="element-162">
            <CardTitle data-id="element-163">Commission Trending</CardTitle>
            <CardDescription data-id="element-164">
              Monthly breakdown of BPS vs CDS commissions
            </CardDescription>
          </CardHeader>
          <CardContent className="pl-0" data-id="element-165">
            <div className="h-[350px] w-full" data-id="element-166">
              <ResponsiveContainer width="100%" height="100%" data-id="element-167">
                <BarChart data={monthlyData} margin={{
                top: 20,
                right: 30,
                left: 20,
                bottom: 5
              }} data-id="element-168">
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" data-id="element-169" />
                  <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{
                  fill: '#64748b',
                  fontSize: 12
                }} dy={10} data-id="element-170" />
                  <YAxis axisLine={false} tickLine={false} tick={{
                  fill: '#64748b',
                  fontSize: 12
                }} tickFormatter={value => `$${value / 1000}k`} dx={-10} data-id="element-171" />
                  <Tooltip formatter={(value: number) => formatCurrency(value)} cursor={{
                  fill: '#f8fafc'
                }} contentStyle={{
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                  fontSize: '14px'
                }} data-id="element-172" />
                  <Legend wrapperStyle={{
                  paddingTop: '20px'
                }} iconType="circle" data-id="element-173" />
                  <Bar dataKey="bpsCommissions" name="BPS Commissions" stackId="a" fill="#0f172a" radius={[0, 0, 4, 4]} maxBarSize={60} data-id="element-174" />
                  <Bar dataKey="cdsCommissions" name="CDS Commissions" stackId="a" fill="#3b82f6" radius={[4, 4, 0, 0]} maxBarSize={60} data-id="element-175" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm border-slate-200/60 flex flex-col" data-id="element-176">
          <CardHeader data-id="element-177">
            <CardTitle data-id="element-178">Top Lead Sources</CardTitle>
            <CardDescription data-id="element-179">MTD performance by volume</CardDescription>
          </CardHeader>
          <CardContent className="flex-1 overflow-y-auto" data-id="element-180">
            <div className="space-y-5" data-id="element-181">
              {metrics.topSources.map((source, idx) => <div key={source.name} className="flex items-center group" data-id="element-182">
                  <div className="w-6 text-sm font-bold text-slate-300 group-hover:text-slate-400 transition-colors" data-id="element-183">
                    {idx + 1}
                  </div>
                  <div className="space-y-1 flex-1" data-id="element-184">
                    <p className="text-sm font-semibold text-slate-900 leading-none" data-id="element-185">
                      {source.name}
                    </p>
                    <p className="text-xs text-slate-500 font-medium" data-id="element-186">
                      {formatCurrency(source.volume)} vol
                    </p>
                  </div>
                  <div className="font-semibold text-sm text-slate-700 bg-slate-100 px-2.5 py-1 rounded-md" data-id="element-187">
                    {source.files}{' '}
                    <span className="text-slate-500 font-normal text-xs" data-id="element-188">
                      files
                    </span>
                  </div>
                </div>)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Row 3: Leaderboard */}
      <Card className="flex flex-col h-[600px] shadow-sm border-slate-200/60" data-id="element-189">
        <CardHeader className="border-b border-slate-100 shrink-0 flex flex-row items-center justify-between pb-4 bg-white rounded-t-xl" data-id="element-190">
          <div data-id="element-191">
            <CardTitle data-id="element-192">User Leaderboard</CardTitle>
            <CardDescription data-id="element-193">
              All users ranked by projected commission
            </CardDescription>
          </div>
          <div className="relative w-72" data-id="element-194">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" data-id="element-195" />
            <Input placeholder="Search users by name or title..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 h-9 bg-slate-50 border-slate-200 focus:bg-white transition-colors" data-id="element-196" />
          </div>
        </CardHeader>
        <CardContent className="flex-1 overflow-y-auto p-0" data-id="element-197">
          <Table data-id="element-198">
            <TableHeader className="sticky top-0 bg-slate-50/90 backdrop-blur-sm shadow-sm z-10 border-b border-slate-200" data-id="element-199">
              <TableRow className="hover:bg-transparent" data-id="element-200">
                <TableHead className="pl-6 w-[350px] font-semibold text-slate-600" data-id="element-201">
                  User
                </TableHead>
                <TableHead className="font-semibold text-slate-600" data-id="element-202">
                  Model
                </TableHead>
                <TableHead className="text-right font-semibold text-slate-600" data-id="element-203">
                  Funded (MTD)
                </TableHead>
                <TableHead className="text-right font-semibold text-slate-600" data-id="element-204">
                  Trend
                </TableHead>
                <TableHead className="text-right pr-6 font-semibold text-slate-600" data-id="element-205">
                  Projected Commission
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody data-id="element-206">
              {filteredUsers.map(user => {
              const trend = user.fundedLastMonth > 0 ? (user.fundedMTD - user.fundedLastMonth) / user.fundedLastMonth * 100 : 0;
              return <TableRow key={user.id} className="hover:bg-slate-50/50 transition-colors" data-id="element-207">
                    <TableCell className="pl-6 py-3" data-id="element-208">
                      <div className="flex items-center gap-4" data-id="element-209">
                        <img src={user.avatar} alt={user.name} className="h-10 w-10 rounded-full bg-slate-200 shadow-sm border border-slate-100" data-id="element-210" />
                        <div data-id="element-211">
                          <div className="font-semibold text-sm text-slate-900" data-id="element-212">
                            {user.name}
                          </div>
                          <div className="text-xs text-slate-500 font-medium" data-id="element-213">
                            {user.title}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell data-id="element-214">
                      <Badge variant={user.model === 'BPS' ? 'default' : 'secondary'} className={cn('text-[10px] uppercase tracking-wider font-bold px-2 py-0.5', user.model === 'BPS' ? 'bg-slate-900 text-white' : 'bg-blue-100 text-blue-700 hover:bg-blue-100')} data-id="element-215">
                        {user.model}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-semibold text-slate-700 text-base" data-id="element-216">
                      {user.fundedMTD}
                    </TableCell>
                    <TableCell className="text-right" data-id="element-217">
                      <div className="flex items-center justify-end text-xs" data-id="element-218">
                        {trend > 0 ? <span className="text-emerald-600 flex items-center font-bold bg-emerald-50 px-2 py-1 rounded-md" data-id="element-219">
                            <ArrowUpRight className="h-3 w-3 mr-1" data-id="element-220" />{' '}
                            {trend.toFixed(0)}%
                          </span> : trend < 0 ? <span className="text-red-600 flex items-center font-bold bg-red-50 px-2 py-1 rounded-md" data-id="element-221">
                            <ArrowDownRight className="h-3 w-3 mr-1" data-id="element-222" />{' '}
                            {Math.abs(trend).toFixed(0)}%
                          </span> : <span className="text-slate-400 font-medium px-2 py-1" data-id="element-223">
                            -
                          </span>}
                      </div>
                    </TableCell>
                    <TableCell className="text-right pr-6 font-bold text-slate-900 text-base" data-id="element-224">
                      {formatCurrency(user.estimatedCommission)}
                    </TableCell>
                  </TableRow>;
            })}
              {filteredUsers.length === 0 && <TableRow data-id="element-225">
                  <TableCell colSpan={5} className="h-32 text-center text-slate-500" data-id="element-226">
                    <div className="flex flex-col items-center justify-center" data-id="element-227">
                      <Search className="h-8 w-8 text-slate-300 mb-2" data-id="element-228" />
                      <p data-id="element-229">No users found matching "{search}"</p>
                    </div>
                  </TableCell>
                </TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>;
}